import { FC, useCallback, useState } from 'react';
import { CodeDisplayBox } from '@medtronic/pecc-react-component-library-js';
import { Grid, Typography } from '@mui/material';
import { StartSessionCommands } from '.';
import connectionInstructionsConfig from '../config';
import { ConnectionInstructionsType, ConnectionType, DEFAULT_PORT, InstructionsConfig, Platform } from '../types';

interface PlatformCommandsProps {
  type: ConnectionInstructionsType;
  instructionsConfig: InstructionsConfig;
  platform: Platform;
}

const getPlatformEnvCommands = (instructionsConfig: InstructionsConfig, platformType: Platform) => {
  const setCommand = platformType === Platform.WINDOWS ? 'SET' : 'export';

  return `${setCommand} AWS_DEFAULT_REGION=${instructionsConfig.awsRegion ?? ''}\n${setCommand} AWS_ACCESS_KEY_ID=${instructionsConfig.accessKeyId ?? ''}\n${setCommand} AWS_SECRET_ACCESS_KEY=${instructionsConfig.secretAccessKey ?? ''}\n${setCommand} AWS_SESSION_TOKEN=${instructionsConfig.sessionToken ?? ''}`;
};

const getAssumeRoleCommand = (roleArn: string, sessionName = 'hdp-session') =>
  `aws sts assume-role --role-arn ${roleArn} --role-session-name ${sessionName}`;

const hasRoleArn = (config: InstructionsConfig) => Boolean(config.roleArn);

const hasSessionCredentials = (config: InstructionsConfig) =>
  Boolean(config.awsRegion) && Boolean(config.accessKeyId) && Boolean(config.secretAccessKey);

const PlatformCommands: FC<PlatformCommandsProps> = ({ instructionsConfig, platform, type }) => {
  const [connectionType, setConnectionType] = useState<ConnectionType>(ConnectionType.SSM_CONNECTION);
  const [port, setPort] = useState<number | null>(DEFAULT_PORT);

  const shouldShowPortCommand = connectionType === ConnectionType.PORT && port !== null;
  const startCommands = connectionInstructionsConfig[type].startCommands(
    instructionsConfig,
    port,
    instructionsConfig.port,
    shouldShowPortCommand,
  );

  const handlePortChange = (port: number | null) => {
    setPort(port);
  };

  const handleConnectionTypeChange = useCallback((connectionType: ConnectionType) => {
    setConnectionType(connectionType);
  }, []);

  const { roleArn } = instructionsConfig;
  const useRole = hasRoleArn(instructionsConfig);
  const useSessionCreds = hasSessionCredentials(instructionsConfig);

  return (
    <Grid container spacing={2}>
      {useRole ? (
        <>
          {roleArn && (
            <Grid size={12}>
              <Typography variant="bodyDefaultStrong">Role ARN</Typography>
              <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all', fontSize: '0.875rem' }}>
                {roleArn}
              </Typography>
              <Typography variant="bodyDefaultStrong">Assume role via CLI</Typography>
              <CodeDisplayBox darkMode code={getAssumeRoleCommand(roleArn)} />
            </Grid>
          )}
        </>
      ) : useSessionCreds ? (
        <Grid size={12}>
          <Typography variant="bodyDefaultStrong">1. Set AWS Environment variables and start the session</Typography>
          <CodeDisplayBox darkMode code={getPlatformEnvCommands(instructionsConfig, platform)} />
        </Grid>
      ) : null}
      <StartSessionCommands
        type={type}
        selectedPort={port}
        startCommand={startCommands[platform]}
        onConnectionTypeChange={handleConnectionTypeChange}
        onPortChange={handlePortChange}
      />
    </Grid>
  );
};

export default PlatformCommands;
